import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  BookOpen,
  Send,
  Sparkles,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  CheckCircle2,
  Clock3,
  AlertTriangle,
  X,
  Filter,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { notificationService } from '../../api'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'In progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'at_risk', label: 'Needs nudge' },
]

const CHANNELS = [
  { value: 'in_app', label: 'In-app' },
  { value: 'email', label: 'Email' },
]

const messageTemplateRequests = new Map()

const getMessageTemplates = (studentId, enrollmentId) => {
  const requestKey = `${studentId}:${enrollmentId}`
  const existingRequest = messageTemplateRequests.get(requestKey)
  if (existingRequest) return existingRequest

  const request = notificationService
    .generateMessageTemplates({
      student_id: Number(studentId),
      enrollment_id: Number(enrollmentId),
    })
    .then((response) => response.data?.templates)
    .catch((error) => {
      messageTemplateRequests.delete(requestKey)
      throw error
    })

  messageTemplateRequests.set(requestKey, request)
  window.setTimeout(() => {
    if (messageTemplateRequests.get(requestKey) === request) {
      messageTemplateRequests.delete(requestKey)
    }
  }, 30_000)
  return request
}

const getProgress = (enr) => Number(enr.progress_percentage ?? enr.progress_percent ?? 0)

const getCourseTitle = (enr) => enr.course_title || enr.course_name || `Course #${enr.course}`

const isCompleted = (enr) => {
  // Lesson progress alone must never count as course completion — required
  // assessments must also be passed (enrollment.status === 'completed').
  const status = (enr.status || '').toLowerCase()
  return status === 'completed'
}

const isAtRisk = (enr) => {
  if (isCompleted(enr)) return false
  const progress = getProgress(enr)
  const enrolledAt = enr.enrolled_at ? new Date(enr.enrolled_at) : null
  const days = enrolledAt ? (Date.now() - enrolledAt.getTime()) / (1000 * 60 * 60 * 24) : 0
  return progress < 25 && days >= 7
}

const statusMeta = (enr) => {
  const status = (enr.status || 'pending').toLowerCase()
  if (isCompleted(enr) || status === 'completed') {
    return { label: 'Completed', tone: 'success', icon: CheckCircle2 }
  }
  if (status === 'approved' || status === 'active') {
    return { label: status === 'active' ? 'Active' : 'Approved', tone: 'brand', icon: Clock3 }
  }
  if (status === 'pending') {
    return { label: 'Pending', tone: 'warning', icon: Clock3 }
  }
  return { label: status, tone: 'muted', icon: BookOpen }
}

const buildAiDrafts = (studentName, enr) => {
  const first = (studentName || 'there').split(' ')[0]
  const course = getCourseTitle(enr)
  const progress = Math.round(getProgress(enr))

  if (isCompleted(enr)) {
    return [
      {
        id: 'congrats',
        label: 'Celebrate completion',
        subject: `Well done on ${course}`,
        message: `Hi ${first},\n\nYou've completed ${course} — strong work. If you'd like a next step, reply here and we can recommend a follow-on path.\n\n— Learner Success`,
      },
      {
        id: 'cert',
        label: 'Certificate follow-up',
        subject: `Certificate for ${course}`,
        message: `Hi ${first},\n\nYour completion of ${course} is recorded. Check your dashboard for certificate status, or message us if anything looks missing.\n\n— Learner Success`,
      },
      {
        id: 'next-goal',
        label: 'Next learning goal',
        subject: `What's next after ${course}?`,
        message: `Hi ${first},\n\nCongratulations on finishing ${course}. When you're ready, tell us what you want to focus on next and we'll help set a clear learning goal.\n\n— Learner Success`,
      },
    ]
  }

  if (isAtRisk(enr) || progress < 15) {
    return [
      {
        id: 'nudge',
        label: 'Gentle re-engagement',
        subject: `Continue ${course}`,
        message: `Hi ${first},\n\nYou're enrolled in ${course} (${progress}% complete). A short session this week can get momentum back — open the course when you're ready, and reply if you need help unblocking.\n\n— Learner Success`,
      },
      {
        id: 'support',
        label: 'Offer support',
        subject: `Help with ${course}?`,
        message: `Hi ${first},\n\nWe noticed limited progress on ${course}. If content access, scheduling, or assessments are blocking you, tell us what's in the way and we'll help.\n\n— Learner Success`,
      },
    ]
  }

  return [
    {
      id: 'encourage',
      label: 'Progress check-in',
      subject: `You're ${progress}% through ${course}`,
      message: `Hi ${first},\n\nNice progress on ${course} — you're at ${progress}%. Keep the streak going when you next have 20–30 minutes.\n\n— Learner Success`,
    },
    {
      id: 'deadline',
      label: 'Keep momentum',
      subject: `Next module on ${course}`,
      message: `Hi ${first},\n\nQuick reminder: your next module in ${course} is ready. Completing it soon keeps your cohort pace on track.\n\n— Learner Success`,
    },
  ]
}

function ProgressRing({ value, size = 52 }) {
  const stroke = 5
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, value))
  const offset = c - (pct / 100) * c

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="enr-progress-ring" aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--border-color)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--primary-brand)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="enr-progress-ring__label"
      >
        {Math.round(pct)}%
      </text>
    </svg>
  )
}

function MessageComposer({
  open,
  onClose,
  student,
  enrollment,
  onSent,
}) {
  const reduceMotion = useReducedMotion()
  const fallbackDrafts = useMemo(
    () => (enrollment ? buildAiDrafts(`${student?.first_name || ''} ${student?.last_name || ''}`.trim() || student?.email, enrollment) : []),
    [student, enrollment],
  )
  const [drafts, setDrafts] = useState([])
  const [draftStatus, setDraftStatus] = useState('idle')
  const [channel, setChannel] = useState('in_app')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [activeDraft, setActiveDraft] = useState(null)
  const [sending, setSending] = useState(false)
  const hasEditedDraft = useRef(false)

  useEffect(() => {
    if (!open || !enrollment) return

    let cancelled = false
    const first = fallbackDrafts[0]
    hasEditedDraft.current = false
    setDrafts(fallbackDrafts)
    setDraftStatus('loading')
    setChannel('in_app')
    setActiveDraft(first?.id || null)
    setSubject(first?.subject || '')
    setMessage(first?.message || '')

    const loadGeminiDrafts = async () => {
      try {
        const generatedDrafts = await getMessageTemplates(student.id, enrollment.id)
        if (cancelled) return
        if (!Array.isArray(generatedDrafts) || generatedDrafts.length === 0) {
          setDraftStatus('fallback')
          return
        }

        setDrafts(generatedDrafts)
        setDraftStatus('ready')
        if (!hasEditedDraft.current) {
          setActiveDraft(generatedDrafts[0].id)
          setSubject(generatedDrafts[0].subject)
          setMessage(generatedDrafts[0].message)
        }
      } catch {
        if (!cancelled) setDraftStatus('fallback')
      }
    }

    loadGeminiDrafts()
    return () => {
      cancelled = true
    }
  }, [open, enrollment, fallbackDrafts, student])

  if (!open || !enrollment) return null

  const handleApplyDraft = (draft) => {
    hasEditedDraft.current = true
    setActiveDraft(draft.id)
    setSubject(draft.subject)
    setMessage(draft.message)
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!message.trim()) {
      toast.error('Message is required')
      return
    }
    setSending(true)
    try {
      const res = await notificationService.createNotification({
        notification_type: channel,
        category: 'enrollment',
        subject: subject.trim() || `Update on ${getCourseTitle(enrollment)}`,
        message: message.trim(),
        user: Number(student.id),
      })
      const created = res.data?.created
      toast.success(created != null ? `Sent to ${created} recipient${created === 1 ? '' : 's'}` : 'Notification sent')
      onSent?.()
      onClose()
    } catch {
      toast.error('Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="enr-composer-backdrop" role="presentation" onClick={onClose}>
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="enr-composer-title"
        className="enr-composer"
        onClick={(e) => e.stopPropagation()}
        initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reduceMotion ? undefined : { opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.22 }}
      >
        <header className="enr-composer__header">
          <div>
            <p className="enr-composer__eyebrow">Message learner</p>
            <h3 id="enr-composer-title">
              {student?.first_name || student?.email || 'Student'}
              <span> · {getCourseTitle(enrollment)}</span>
            </h3>
          </div>
          <button type="button" className="enr-icon-btn" onClick={onClose} aria-label="Close composer">
            <X size={18} />
          </button>
        </header>

        <div className="enr-composer__ai">
          <div className="enr-ai-badge type-ai-label">
            <Sparkles size={12} aria-hidden="true" />
            AI
          </div>
          <p className="type-ai enr-composer__ai-copy" aria-live="polite">
            {draftStatus === 'loading'
              ? 'Gemini is tailoring templates from completion, progress, streak, and next-goal context. You can use the local drafts while it works.'
              : draftStatus === 'ready'
                ? 'Gemini tailored these templates from verified learner activity. Review and edit before sending.'
                : 'Using reliable local templates because Gemini is unavailable. Review and edit before sending.'}
          </p>
          <div className="enr-composer__drafts">
            {drafts.map((draft) => (
              <button
                key={draft.id}
                type="button"
                className={`enr-draft-chip${activeDraft === draft.id ? ' is-active' : ''}`}
                onClick={() => handleApplyDraft(draft)}
              >
                {draft.label}
              </button>
            ))}
          </div>
        </div>

        <form className="enr-composer__form" onSubmit={handleSend}>
          <div className="enr-composer__channels" role="group" aria-label="Delivery channel">
            {CHANNELS.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`enr-channel${channel === c.value ? ' is-active' : ''}`}
                onClick={() => setChannel(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <label className="enr-field">
            <span>Subject</span>
            <input
              value={subject}
              onChange={(e) => {
                hasEditedDraft.current = true
                setSubject(e.target.value)
              }}
              placeholder="Subject line"
            />
          </label>

          <label className="enr-field">
            <span>Message</span>
            <textarea
              value={message}
              onChange={(e) => {
                hasEditedDraft.current = true
                setMessage(e.target.value)
              }}
              rows={7}
              required
              placeholder="Write a clear, specific note…"
            />
          </label>

          <footer className="enr-composer__footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={sending}>
              <Send size={16} />
              {sending ? 'Sending…' : 'Send notification'}
            </button>
          </footer>
        </form>
      </motion.div>
    </div>
  )
}

function EnrollmentCard({ enr, expanded, onToggle, onMessage, index }) {
  const reduceMotion = useReducedMotion()
  const progress = getProgress(enr)
  const meta = statusMeta(enr)
  const StatusIcon = meta.icon
  const atRisk = isAtRisk(enr)
  const courseId = enr.course_id || enr.course

  return (
    <motion.article
      layout
      className={`enr-card${expanded ? ' is-expanded' : ''}${atRisk ? ' is-at-risk' : ''}`}
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.2), duration: 0.28 }}
    >
      <button
        type="button"
        className="enr-card__main"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <ProgressRing value={progress} />
        <div className="enr-card__body">
          <div className="enr-card__top">
            <h4>{getCourseTitle(enr)}</h4>
            <span className={`enr-status enr-status--${meta.tone}`}>
              <StatusIcon size={12} aria-hidden="true" />
              {meta.label}
            </span>
          </div>
          <p className="enr-card__meta">
            Enrolled {enr.enrolled_at ? new Date(enr.enrolled_at).toLocaleDateString() : '—'}
            {atRisk && (
              <span className="enr-risk">
                <AlertTriangle size={12} aria-hidden="true" /> Needs nudge
              </span>
            )}
          </p>
          <div className="enr-card__track" aria-hidden="true">
            <motion.div
              className="enr-card__fill"
              initial={false}
              animate={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
        <ChevronDown size={18} className={`enr-card__chevron${expanded ? ' is-open' : ''}`} aria-hidden="true" />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="enr-card__panel"
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduceMotion ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <div className="enr-card__actions">
              {courseId ? (
                <Link to={`/courses/${courseId}`} className="btn btn-outline enr-action-btn">
                  <ExternalLink size={15} />
                  Open course
                </Link>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary enr-action-btn"
                onClick={() => onMessage(enr)}
              >
                <MessageSquare size={15} />
                Message about this
              </button>
            </div>
            {atRisk && (
              <div className="enr-insight">
                <div className="enr-ai-badge type-ai-label">
                  <Sparkles size={12} aria-hidden="true" />
                  AI
                </div>
                <p className="type-ai">
                  Low progress after a week enrolled — a short check-in usually restarts completion.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  )
}

export default function CourseEnrollmentsPanel({ student, enrollments = [] }) {
  const [filter, setFilter] = useState('all')
  const [expandedId, setExpandedId] = useState(null)
  const [composerEnr, setComposerEnr] = useState(null)

  const stats = useMemo(() => {
    const total = enrollments.length
    const completed = enrollments.filter(isCompleted).length
    const atRisk = enrollments.filter(isAtRisk).length
    const avg =
      total === 0
        ? 0
        : Math.round(enrollments.reduce((sum, e) => sum + getProgress(e), 0) / total)
    return { total, completed, atRisk, avg }
  }, [enrollments])

  const filtered = useMemo(() => {
    if (filter === 'completed') return enrollments.filter(isCompleted)
    if (filter === 'at_risk') return enrollments.filter(isAtRisk)
    if (filter === 'active') return enrollments.filter((e) => !isCompleted(e))
    return enrollments
  }, [enrollments, filter])

  const openGeneralComposer = () => {
    if (!enrollments.length) {
      toast.error('No enrollments to reference')
      return
    }
    const target = enrollments.find(isAtRisk) || enrollments[0]
    setComposerEnr(target)
  }

  return (
    <section className="enr-panel">
      <header className="enr-panel__header">
        <div className="enr-panel__title">
          <div className="enr-panel__icon">
            <BookOpen size={18} aria-hidden="true" />
          </div>
          <div>
            <h3>Course enrollments</h3>
            <p>Track progress, surface at-risk courses, and message the learner in context.</p>
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={openGeneralComposer} disabled={!enrollments.length}>
          <Send size={16} />
          Message student
        </button>
      </header>

      <div className="enr-panel__stats" aria-label="Enrollment summary">
        <div className="enr-stat">
          <span className="enr-stat__value">{stats.total}</span>
          <span className="enr-stat__label">Enrolled</span>
        </div>
        <div className="enr-stat">
          <span className="enr-stat__value">{stats.completed}</span>
          <span className="enr-stat__label">Completed</span>
        </div>
        <div className="enr-stat">
          <span className="enr-stat__value">{stats.avg}%</span>
          <span className="enr-stat__label">Avg progress</span>
        </div>
        <div className={`enr-stat${stats.atRisk ? ' enr-stat--alert' : ''}`}>
          <span className="enr-stat__value">{stats.atRisk}</span>
          <span className="enr-stat__label">Needs nudge</span>
        </div>
      </div>

      <div className="enr-panel__filters" role="tablist" aria-label="Filter enrollments">
        <Filter size={14} className="enr-panel__filter-icon" aria-hidden="true" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`enr-filter${filter === f.id ? ' is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id === 'at_risk' && stats.atRisk > 0 ? ` (${stats.atRisk})` : ''}
          </button>
        ))}
      </div>

      <div className="enr-panel__list">
        {filtered.length === 0 ? (
          <div className="enr-empty">
            <BookOpen size={22} aria-hidden="true" />
            <p>{enrollments.length === 0 ? 'Not enrolled in any courses yet.' : 'No enrollments match this filter.'}</p>
          </div>
        ) : (
          filtered.map((enr, index) => (
            <EnrollmentCard
              key={enr.id}
              enr={enr}
              index={index}
              expanded={expandedId === enr.id}
              onToggle={() => setExpandedId((prev) => (prev === enr.id ? null : enr.id))}
              onMessage={setComposerEnr}
            />
          ))
        )}
      </div>

      <AnimatePresence>
        {composerEnr && (
          <MessageComposer
            open={Boolean(composerEnr)}
            enrollment={composerEnr}
            student={student}
            onClose={() => setComposerEnr(null)}
          />
        )}
      </AnimatePresence>
    </section>
  )
}
